import {
  AudioDescriptionConfigDTO,
  DialogueListConfigDTO,
  ExtendedMetadataDTO,
  LanguageConfigDTO,
  NewProjectConfigurationDTO
} from "../../models/interfaces/dtos/newProjectConfigurations.dto.ts";
import {NewProjectConfiguration, NewProjectConfigurations} from "../../models/interfaces/newProjectConfigurations.ts";
import {ILanguageConfiguration} from "../../models/interfaces/languageConfiguration.interface.ts";
import {
  IDialogueListConfiguration,
  IDialogueListExtendedMetadata
} from "../../models/interfaces/dialogueListConfiguration.interface.ts";
import {GranularityEnum} from "../../models/enums/granularity.enum.ts";
import {VisualTagsEnum} from "../../models/enums/visualTags.enum.ts";
import {IAudioDescriptionConfiguration} from "../../models/interfaces/audioDescriptionConfiguration.interface.ts";

export const transformLanguageConfigDto = (dto: LanguageConfigDTO): ILanguageConfiguration => {
  return {
    maxCharactersPerLine: dto.maxCharactersPerLine,
    minFramesGap: dto.minFramesGap,
    maxSubtitleTime: dto.maxSubtitleTime,
    maxLines: dto.maxLines,
    minSubtitleTimeInFrames: dto.minSubtitleTimeInFrames,
  }
}

export const transformSourceTranscriptionConfigDto = (
  dto: LanguageConfigDTO,
): NewProjectConfiguration["config"]["sourceTranscriptionConfig"] => ({
  ...transformLanguageConfigDto(dto),
  // Null is the API's unset value; UI presets use undefined to preserve the current choice.
  keepSongs: dto.keepSongs ?? undefined,
});

export const transformExtendedMetadataDto = (dto: ExtendedMetadataDTO): IDialogueListExtendedMetadata => {
  return {
    granularity: dto.granularity as GranularityEnum,
    visualClassifiers: dto.visualClassifiers as VisualTagsEnum[],
    shotChangeDetectionSensitivity: dto.shotChangeDetectionSensitivity,
  }
}

export const transformDialogueListConfigDto = (dto: DialogueListConfigDTO): IDialogueListConfiguration => {
  return {
    additionalLanguages: dto?.additionalLanguages,
    songsRecognition: dto?.songsRecognition,
    extendedMetadata: dto?.extendedMetadata ? transformExtendedMetadataDto(dto.extendedMetadata) : undefined,
  }
}

export const transformAudioDescriptionConfigDto = (dto: AudioDescriptionConfigDTO): IAudioDescriptionConfiguration => {
  return {
    targetLanguages: dto.targetLanguages,
  }
}

export const transformNewProjectConfigurationDTO = (dto: NewProjectConfigurationDTO): NewProjectConfiguration => {
  return {
    id: dto._id,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    clientId: dto.clientId,
    name: dto.name,
    sourceLanguage: dto.sourceLanguage,
    config: {
      sourceTranscriptionConfig: transformSourceTranscriptionConfigDto(dto.config.sourceTranscriptionConfig),
      subtitles: Object.fromEntries(
        Object.entries(dto.config.subtitles).map(([language, configDto]: [string, LanguageConfigDTO]) => [
          language,
          transformLanguageConfigDto(configDto as LanguageConfigDTO)
        ])
      ),
      shotChangeSync: !!dto.config?.shotChangeSync,
      dubbingScript: !!dto.config?.dubbingScript,
      dialogueListConfig: dto.config?.dialogueListConfig ? transformDialogueListConfigDto(dto.config.dialogueListConfig) : undefined,
      audioDescriptions: dto.config?.audioDescriptions ? transformAudioDescriptionConfigDto(dto.config.audioDescriptions) : undefined,
    }
  }
}


export const transformNewProjectConfigurationsDto = (dto: NewProjectConfigurationDTO[]): NewProjectConfigurations => {
  return dto.reduce((acc: NewProjectConfigurations, item: NewProjectConfigurationDTO) => {
    const transformedItem: NewProjectConfiguration = transformNewProjectConfigurationDTO(item);
    acc[transformedItem.id] = transformedItem;
    return acc;
  }, {});
}